/**
 * Stub frontend-only dedupe storage for VI-548 exit-intent reminders.
 *
 * Two layers:
 *   - localStorage: tracks whether the candidate has already requested a
 *     reminder today for a given applicantGuid. Acts as a frontend proxy for
 *     the server-side dedupe rule in the AC (one reminder email per calendar
 *     day per applicant). Source of truth will move to the upstream Job
 *     Application API in the follow-up backend ticket; this util will then
 *     be replaced by an API call.
 *   - sessionStorage: tracks whether the candidate has already interacted
 *     with the exit-intent modal on a given page during the current tab
 *     session, so we don't repeatedly re-show it once dismissed.
 */

export type ExitIntentPage = 'landing' | 'interview';

const REMINDER_KEY_PREFIX = 'exit_intent_reminder';
const SESSION_KEY_PREFIX = 'exit_intent_interacted';

const safeLocalStorage = (): Storage | null => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
};

const safeSessionStorage = (): Storage | null => {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
};

const todayKey = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const reminderKey = (applicantGuid: string): string =>
  `${REMINDER_KEY_PREFIX}_${applicantGuid}_${todayKey()}`;

const sessionKey = (applicantGuid: string, page: ExitIntentPage): string =>
  `${SESSION_KEY_PREFIX}_${applicantGuid}_${page}`;

export function hasReminderToday(applicantGuid: string): boolean {
  const store = safeLocalStorage();
  if (!store || !applicantGuid) return false;
  return store.getItem(reminderKey(applicantGuid)) === 'true';
}

export function markReminderToday(applicantGuid: string): void {
  const store = safeLocalStorage();
  if (!store || !applicantGuid) return;
  try {
    store.setItem(reminderKey(applicantGuid), 'true');
  } catch {
    // ignore quota / privacy-mode failures
  }
}

export function hasInteractedThisSession(
  applicantGuid: string,
  page: ExitIntentPage,
): boolean {
  const store = safeSessionStorage();
  if (!store || !applicantGuid) return false;
  return store.getItem(sessionKey(applicantGuid, page)) === 'true';
}

export function markInteractedThisSession(
  applicantGuid: string,
  page: ExitIntentPage,
): void {
  const store = safeSessionStorage();
  if (!store || !applicantGuid) return;
  try {
    store.setItem(sessionKey(applicantGuid, page), 'true');
  } catch {
    // ignore quota / privacy-mode failures
  }
}
